/**
 * Match helpers: status and unread derived on the fly from match.chat
 */

import type { Match, MatchStatus } from "@features/messaging/types";
import type { Chat } from "@fitties/shared";

/**
 * Returns whether the chat is unread for the given user.
 * Uses last_message_seen_by_uids and last_message_sent_by_uid; treat missing as read.
 */
export function isChatUnread(chat: Chat | undefined, userId: string): boolean {
  if (!chat) {
    return false;
  }
  const seenBy = chat.last_message_seen_by_uids;
  const sentBy = chat.last_message_sent_by_uid;
  if (!seenBy || sentBy === undefined) {
    return false;
  }
  return !seenBy.includes(userId) && sentBy !== userId;
}

/**
 * Derives match status from match.chat: "existing" when there is a last message timestamp, else "new".
 */
export function deriveMatchStatus(match: Match): MatchStatus {
  return match.chat?.last_message_sent_timestamp ? "existing" : "new";
}

/**
 * Returns a new array of matches sorted by last message time (most recent first).
 * Matches without a chat or last_message_sent_timestamp sort to the end.
 */
export function sortMatchesByLastMessageTime(matches: Match[]): Match[] {
  return [...matches].sort((a, b) => {
    const timeA = a.chat?.last_message_sent_timestamp?.getTime() ?? 0;
    const timeB = b.chat?.last_message_sent_timestamp?.getTime() ?? 0;
    return timeB - timeA;
  });
}
